/**
 * List of operations for visual builder
 */

import {
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  RocketLaunchIcon,
  ServerIcon,
  SparklesIcon,
  TicketIcon,
  VariableIcon
} from '@heroicons/react/24/outline'
import { get } from 'lodash'

import { CodeEditor } from '@/components/Icons'
import { APPBUILDER_DEVELOPMENT_FEATURES, BUILDER_PRO_CODE_APPS, WORKSHOP_AGENTS } from '@/constants/featureToggles'
import AdaptiveCardIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/AdaptiveCardIcon'
import APIRequestIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/APIRequestIcon'
import DataStorageIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/DataStorageIcon'
import DataTransformIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/DataTransformIcon'
import DateIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/DateIcon'
import DecisionIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/DecisionIcon'
import DeleteNotificationIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/DeleteNotificationIcon'
import FilterIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/FilterIcon'
import FindIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/FindIcon'
import GroupIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/GroupIcon'
import LogIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/LogIcon'
import LoopIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/LoopIcon'
import NotificationIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/NotificationIcon'
import PickIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/PickIcon'
import ResponseIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/ResponseIcon'
import RunFeatureIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/RunFeatureIcon'
import SortIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/SortIcon'
import UserLookupIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/operations/UserLookupIcon'
import { OperationType, StepType } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import getInitialStepValuesByType from './getInitialStepValues'
import messages from './messages'
import notificationMessages from './Notification/messages'

type Message = {
  id: string
  defaultMessage: string
}

type Operation = {
  name: StepType
  type: OperationType
  draggable: boolean
  label?: Message
  subLabel?: Message
  description?: Message
  icon?: JSX.Element
  defaultStepData?: any
  horizontalOffset?: number
  tooltip?: string
  feature?: boolean
}

type FeatureFlags = { [key: string]: string | number | boolean }

const filterByFeatureFlag = (operations: Operation[]) =>
  operations.filter(operation => {
    const feature = get(operation, 'feature')
    if (feature === undefined) {
      return operation
    } else {
      return feature
    }
  })

const filterAppUiStep = (operations: Operation[]) =>
  operations.filter(operation => operation.name !== StepType.NotificationView)

const SparkleIcon = () => <SparklesIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />

const getOperations = (features?: FeatureFlags, hideAppUiStep: boolean = false, agentsFlagOn: boolean = false) => {
  const operations: Operation[] = [
    {
      name: StepType.Boolean,
      label: messages.boolean,
      subLabel: messages.trueFalse,
      draggable: true,
      description: messages.booleanDescription,
      type: OperationType.operation,
      icon: <DecisionIcon />,
      defaultStepData: getInitialStepValuesByType({ type: StepType.Boolean })
    },
    {
      name: StepType.DataSource,
      type: OperationType.dataSource,
      draggable: true
    },
    {
      name: StepType.WorkgridDataSource,
      type: OperationType.dataSource,
      draggable: true
    },
    {
      name: StepType.Log,
      label: messages.log,
      draggable: true,
      description: messages.logDescription,
      type: OperationType.operation,
      icon: <LogIcon />
    },
    {
      name: StepType.SetConfigVariable,
      label: messages.setConfigVariable,
      draggable: true,
      description: messages.setConfigVariableDescription,
      type: OperationType.operation,
      icon: <VariableIcon className="h-6 w-6 text-blue-700" />
    },
    {
      name: StepType.FileUpload,
      label: messages.uploadFile,
      draggable: true,
      description: messages.uploadFileDescription,
      type: OperationType.operation,
      icon: <DocumentArrowUpIcon className="h-6 w-6 text-blue-700" />
    },
    {
      name: StepType.FileDownload,
      label: messages.readFile,
      draggable: true,
      description: messages.readFileDescription,
      type: OperationType.operation,
      icon: <DocumentArrowDownIcon className="h-6 w-6 text-blue-700" />
    },
    {
      name: StepType.Loop,
      label: messages.loop,
      draggable: true,
      description: messages.loopDescription,
      type: OperationType.operation,
      icon: <LoopIcon />,
      defaultStepData: getInitialStepValuesByType({ type: StepType.Loop })
    },
    {
      name: StepType.Notification,
      label: notificationMessages.sendNotification,
      description: notificationMessages.sendNotificationDescription,
      horizontalOffset: -20,
      type: OperationType.notificationOperation,
      draggable: true,
      icon: <NotificationIcon />
    },
    {
      name: StepType.NotificationDelete,
      label: notificationMessages.deleteNotification,
      type: OperationType.notificationOperation,
      draggable: true,
      icon: <DeleteNotificationIcon />
    },
    {
      name: StepType.NotificationDateFormatter,
      label: notificationMessages.dateTimeFormatter,
      type: OperationType.notificationOperation,
      draggable: true,
      icon: <DateIcon />,
      defaultStepData: getInitialStepValuesByType({ type: StepType.NotificationDateFormatter })
    },
    {
      name: StepType.AdaptiveCard,
      label: messages.adaptiveCard,
      draggable: true,
      type: OperationType.advancedOperation,
      icon: <AdaptiveCardIcon />
    },
    {
      name: StepType.CustomCode,
      label: messages.customCode,
      draggable: true,
      type: OperationType.advancedOperation,
      icon: <CodeEditor />
    },
    {
      name: StepType.APIRequest,
      label: messages.apiRequest,
      description: messages.apiRequestDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      icon: <APIRequestIcon />
    },
    {
      name: StepType.DataStorage,
      label: messages.dataStorage,
      type: OperationType.advancedOperation,
      draggable: true,
      icon: <DataStorageIcon />
    },
    {
      name: StepType.DataTransform,
      label: messages.dataTransform,
      description: messages.dataTransformDescription,
      type: OperationType.dataTransformOperation,
      draggable: true,
      icon: <DataTransformIcon />
    },
    {
      name: StepType.DataTransform,
      label: messages.filter,
      description: messages.dataTransformFilterDescription,
      type: OperationType.dataTransformOperation,
      draggable: true,
      icon: <FilterIcon />,
      defaultStepData: {
        actionName: 'filter'
      }
    },
    {
      name: StepType.DataTransform,
      label: messages.pick,
      description: messages.dataTransformPickDescription,
      type: OperationType.dataTransformOperation,
      draggable: true,
      icon: <PickIcon />,
      defaultStepData: {
        actionName: 'pick'
      }
    },
    {
      name: StepType.DataTransform,
      label: messages.find,
      description: messages.dataTransformFindDescription,
      type: OperationType.dataTransformOperation,
      draggable: true,
      icon: <FindIcon />,
      defaultStepData: {
        actionName: 'find'
      }
    },
    {
      name: StepType.DataTransform,
      label: messages.group,
      description: messages.dataTransformGroupDescription,
      type: OperationType.dataTransformOperation,
      draggable: true,
      icon: <GroupIcon />,
      defaultStepData: {
        actionName: 'group-by'
      }
    },
    {
      name: StepType.DataTransform,
      label: messages.sort,
      description: messages.dataTransformSortDescription,
      type: OperationType.dataTransformOperation,
      draggable: true,
      icon: <SortIcon />,
      defaultStepData: {
        actionName: 'sort-by'
      }
    },
    {
      name: StepType.Response,
      label: messages.response,
      description: messages.responseDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      tooltip: 'This is a terminal operation and will end the workflow.',
      icon: <ResponseIcon />
    },
    {
      name: StepType.RunFeature,
      label: messages.runFeature,
      description: messages.runFeatureDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      feature: !get(features, WORKSHOP_AGENTS, false),
      icon: <RunFeatureIcon />
    },
    {
      name: StepType.RunFeature,
      label: messages.runCapability,
      description: messages.runCapabilityDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      feature: !!get(features, WORKSHOP_AGENTS, false),
      icon: <RunFeatureIcon />
    },
    {
      name: StepType.DeployFeature,
      label: messages.deployFeature,
      description: messages.deployFeatureStepDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      feature: !get(features, WORKSHOP_AGENTS, false),
      icon: <RocketLaunchIcon className="h-6 w-6 text-blue-700" />
    },
    {
      name: StepType.DeployFeature,
      label: messages.deployCapability,
      description: messages.deployCapabilityStepDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      feature: !!get(features, WORKSHOP_AGENTS, false),
      icon: <RocketLaunchIcon className="h-6 w-6 text-blue-700" />
    },
    {
      name: StepType.InvokeLambda,
      label: messages.invokeLambda,
      description: messages.invokeLambdaStepDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      feature: !!get(features, BUILDER_PRO_CODE_APPS, false),
      icon: <ServerIcon className="h-6 w-6 text-blue-700" />
    },
    {
      name: StepType.GenerateMicroappToken,
      label: messages.generateMicroappToken,
      description: messages.generateMicroappStepDescription,
      type: OperationType.advancedOperation,
      draggable: true,
      feature: !!get(features, BUILDER_PRO_CODE_APPS, false),
      icon: <TicketIcon className="h-6 w-6 text-blue-700" />
    },
    {
      name: StepType.UserLookup,
      label: messages.userLookup,
      draggable: true,
      description: messages.userLookupDescription,
      type: OperationType.advancedOperation,
      icon: <UserLookupIcon />
    },
    {
      name: StepType.NotificationView,
      label: agentsFlagOn ? messages.sendAgentUINotification : messages.sendAppUINotification,
      horizontalOffset: -20,
      description: messages.sendAppUINotificationDescription,
      type: OperationType.notificationOperation,
      draggable: true,
      feature: !!get(features, APPBUILDER_DEVELOPMENT_FEATURES, false),
      icon: <NotificationIcon />
    },
    {
      name: StepType.DocumentTextSplitter,
      label: messages.documentTextSplitter,
      description: messages.documentTextSplitterDescription,
      type: OperationType.aiOperation,
      draggable: true,
      icon: <SparkleIcon />
    },
    {
      name: StepType.DocumentSearchStorage,
      label: messages.searchStorage,
      type: OperationType.aiOperation,
      draggable: true,
      icon: <SparkleIcon />
    }
  ]

  let filteredOperations = operations
  if (features) {
    filteredOperations = filterByFeatureFlag(operations)
  }
  if (hideAppUiStep) {
    filteredOperations = filterAppUiStep(filteredOperations)
  }
  return filteredOperations
}
export default getOperations
